// Public Ticket Reply Page - Customers can view and reply to their tickets
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Avatar,
  Stack,
  Paper,
  Chip,
  Alert,
  Container,
} from '@mui/material';
import { Send, ArrowBack, CheckCircle, Schedule } from '@mui/icons-material';
import { ticketsService } from '@/services/collections/tickets';
import { ticketResponsesService } from '@/services/collections/ticketResponses';
import { organizationsService } from '@/services/collections/organizations';
import { formatRelativeTime } from '@/services/firebase/firestore';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { Ticket, TicketResponse, Organization, BrandingSettings } from '@/types';
import { DEFAULT_BRANDING } from '@/types';

const STATUS_DISPLAY: Record<string, { label: string; color: any; icon: React.ReactNode }> = {
  new: { label: 'Received', color: 'info', icon: <Schedule /> },
  open: { label: 'In Progress', color: 'warning', icon: <Schedule /> },
  pending: { label: 'Awaiting Info', color: 'default', icon: <Schedule /> },
  resolved: { label: 'Resolved', color: 'success', icon: <CheckCircle /> },
  closed: { label: 'Closed', color: 'default', icon: <CheckCircle /> },
};

export const TicketReplyPage = () => {
  const { orgSlug, ticketId } = useParams<{ orgSlug: string; ticketId: string }>();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [branding, setBranding] = useState<BrandingSettings>(DEFAULT_BRANDING);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [responses, setResponses] = useState<TicketResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [replyContent, setReplyContent] = useState('');
  const [sending, setSending] = useState(false);
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [error, setError] = useState('');

  // Fetch organization, ticket and responses
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    const fetchData = async () => {
      if (!ticketId || !orgSlug) return;

      setLoading(true);
      try {
        // Fetch organization by slug
        const org = await organizationsService.getBySlug(orgSlug);
        if (!org) {
          setError('Organization not found');
          setLoading(false);
          return;
        }
        setOrganization(org);
        setBranding(org.branding || DEFAULT_BRANDING);

        // Fetch ticket and verify it belongs to this organization
        const data = await ticketsService.getById(ticketId);
        if (!data || data.organizationId !== org.id) {
          setError('Ticket not found');
          setLoading(false);
          return;
        }
        setTicket(data);

        // Subscribe to public responses only
        unsubscribe = ticketResponsesService.subscribe(
          ticketId,
          (newResponses) => {
            // Filter out internal notes
            setResponses(newResponses.filter((r) => !r.isInternal));
          },
          (err) => console.error('Error subscribing to responses:', err)
        );
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load ticket');
      } finally {
        setLoading(false);
      }
    };

    fetchData();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [ticketId, orgSlug]);

  // Verify email matches ticket submitter
  const handleVerifyEmail = () => {
    if (!ticket) return;
    if (email.toLowerCase() === ticket.submitterEmail.toLowerCase()) {
      setEmailVerified(true);
      setError('');
    } else {
      setError('Email does not match the ticket submitter');
    }
  };

  // Send reply
  const handleSendReply = async () => {
    if (!ticket || !replyContent.trim() || !organization) return;

    setSending(true);
    try {
      await ticketResponsesService.createUserResponse(
        ticket.id,
        organization.id,
        ticket.projectId,
        replyContent.trim(),
        ticket.submitterEmail,
        ticket.submitterName
      );

      setReplyContent('');

      // Update ticket status to open if it was pending
      if (ticket.status === 'pending' || ticket.status === 'resolved') {
        await ticketsService.updateStatus(ticket.id, 'open');
        setTicket({ ...ticket, status: 'open' });
      }
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return <LoadingSpinner fullPage message="Loading ticket..." />;
  }

  if (error && !ticket) {
    return (
      <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="error" gutterBottom>
          {error}
        </Typography>
        <Button onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Container>
    );
  }

  if (!ticket) return null;

  const statusInfo = STATUS_DISPLAY[ticket.status] || STATUS_DISPLAY.new;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(`/p/${organization?.slug}`)}>
          Back to Portal
        </Button>
      </Box>

      {/* Ticket Info Card */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ticket #{ticket.ticketNumber || ticket.id.slice(0, 6)}
              </Typography>
              <Typography variant="h5" fontWeight="bold">
                {ticket.subject}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Submitted {formatRelativeTime(ticket.createdAt)}
              </Typography>
            </Box>
            <Chip
              icon={statusInfo.icon as any}
              label={statusInfo.label}
              color={statusInfo.color}
              variant="outlined"
            />
          </Box>

          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {ticket.description}
            </Typography>
          </Paper>
        </CardContent>
      </Card>

      {/* Email Verification (if not verified) */}
      {!emailVerified && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Verify Your Email
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              To view replies and respond to this ticket, please verify your email address.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Your Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                placeholder="Enter the email you used to submit this ticket"
              />
              <Button variant="contained" onClick={handleVerifyEmail} disabled={!email}>
                Verify
              </Button>
            </Stack>
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Conversation (only shown after email verification) */}
      {emailVerified && (
        <>
          <Card sx={{ mb: 4 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Conversation
              </Typography>

              {responses.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                  No replies yet. Our team will respond soon.
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {responses.map((response) => (
                    <Paper
                      key={response.id}
                      elevation={0}
                      sx={{
                        p: 2,
                        bgcolor: response.isAgent ? 'primary.50' : 'grey.100',
                        borderLeft: 3,
                        borderColor: response.isAgent ? 'primary.main' : 'grey.400',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            fontSize: '0.875rem',
                            bgcolor: response.isAgent ? branding.primaryColor : 'grey.500',
                          }}
                        >
                          {response.authorName[0]}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="subtitle2">
                            {response.isAgent ? `${organization?.name || 'Support'} Team` : 'You'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatRelativeTime(response.createdAt)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', pl: 4.5 }}>
                        {response.content}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              )}
            </CardContent>
          </Card>

          {/* Reply Box */}
          {ticket.status !== 'closed' && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Reply
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  placeholder="Type your reply..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    endIcon={<Send />}
                    onClick={handleSendReply}
                    disabled={!replyContent.trim() || sending}
                    sx={{ bgcolor: branding.primaryColor }}
                  >
                    {sending ? 'Sending...' : 'Send Reply'}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          )}

          {ticket.status === 'closed' && (
            <Alert severity="info">
              This ticket has been closed. If you need further assistance, please submit a new request.
            </Alert>
          )}
        </>
      )}
    </Container>
  );
};
